/**
 * TAP data-read layer. Binds @repo/content-db readers to the two Firestore
 * instances TAP needs:
 *   - `contentDb` = content-db — shared content (courses, collections, resources, roles, content-actions, ...)
 *   - `db`        = tap-db     — TAP user docs, annotations, and subscriptions
 *
 * We only read; all writes happen through Cloud Functions.
 */
import {
    COLLECTION_TYPES,
    createEntityReader,
    fetchUserSubscriptions,
} from '@repo/content-db';
import {
    addDoc,
    collection,
    deleteDoc,
    doc,
    getDoc,
    getDocs,
    query,
    setDoc,
    where,
} from 'firebase/firestore';
import { auth, contentDb, db as tapDb } from './firebase/firebase.js';
import { tenantID } from '@repo/config/Settings';

const contentReader = createEntityReader({
    db: contentDb,
    namespace: 'content-db',
});

const tapReader = createEntityReader({
    db: tapDb,
    namespace: 'tap-db',
    // TAP users are never admins; tap rules hide archived docs from them.
    canReadArchived: () => false,
});

// --- content-db shared content database ---
export const getEntity = contentReader.getEntity;
export const getEntities = contentReader.getEntities;
export const getEntitiesWhere = contentReader.getEntitiesWhere;
export { COLLECTION_TYPES };

// @repo/book-accordion reads read-only-titles subcollections off the content
// Firestore. Education exposes a `getFirestore()` helper; mirror it so the
// accordion's context binding is identical across apps.
export function getFirestore() {
    return contentDb;
}

const entityBreadcrumbCache = {};

/**
 * Walk the content graph upwards from a resource/collection/course, collecting
 * ancestor entities until we hit the root. Mirrors Education's implementation.
 */
export async function getEntityBreadcrumb(data, breadcrumb = [], noCache = false) {
    if (!noCache && entityBreadcrumbCache[`c_${data.id}`]) {
        return entityBreadcrumbCache[`c_${data.id}`].concat(breadcrumb);
    }

    const ent = await Promise.any([
        getEntity(data, COLLECTION_TYPES.COLLECTIONS),
        getEntity(data, COLLECTION_TYPES.COURSES),
        getEntity(data, COLLECTION_TYPES.RESOURCES),
    ]).catch(e => {
        console.error('[tap-db] getEntityBreadcrumb any failed', e);
        // A missing doc is a plain Error; a failed read carries a Firestore code.
        if (e.errors?.some(err => err?.code)) breadcrumb.incomplete = true;
        return null;
    });

    if (!ent?.exists?.()) return breadcrumb;

    const d = ent.data();
    d.id = ent.id;
    breadcrumb.unshift(d);

    const [resourceParent, collectionParent] = await Promise.all([
        getEntitiesWhere(
            { needles: [{ field: 'items', operator: 'array-contains', match: data.id }], limit: 1 },
            COLLECTION_TYPES.RESOURCES,
        ),
        getEntitiesWhere(
            { needles: [{ field: 'items', operator: 'array-contains', match: data.id }], limit: 1 },
            COLLECTION_TYPES.COLLECTIONS,
        ),
    ]);

    if (!resourceParent.empty) {
        return getEntityBreadcrumb({ id: resourceParent.docs[0].id }, breadcrumb, noCache);
    }
    if (!collectionParent.empty) {
        return getEntityBreadcrumb({ id: collectionParent.docs[0].id }, breadcrumb, noCache);
    }
    if (!noCache) entityBreadcrumbCache[`c_${breadcrumb[breadcrumb.length - 1].id}`] = breadcrumb;
    return breadcrumb;
}

// --- Classroom-only stubs (TAP has no classrooms) ---

export async function getSubmissionsResourcesForUser() {
    return [];
}

/**
 * Persist per-user settings onto `tap-db/users/{uid}`.
 */
export async function setUserData(data) {
    if (!auth.currentUser || !data) return false;

    try {
        await setDoc(doc(tapDb, 'users', auth.currentUser.uid), data, { merge: true });
        return true;
    } catch (err) {
        console.warn('[tap-db] setUserData failed', err);
        return false;
    }
}

export async function updateEntity() {
    // Writes to the content DB go through Cloud Functions, not directly.
}

// --- Annotation persistence, tap-db/annotations ---
// Isolated from Education's annotations in (default). Dual-access users get
// two independent annotation streams — one per app — which matches how
// tap-db/users/{uid} already isolates the TAP user doc.

const ANNOTATIONS = 'annotations';
const ARCHIVED_ANNOTATIONS = 'archived-annotations';

/**
 * Fetch a single annotation matching all provided fields (sid required).
 * Returns the Firestore doc snapshot, or `false` if not found (matches the
 * Education contract — the viewer checks `.exists()`).
 */
export async function getAnnotation(data) {
    if (!data?.sid) throw new Error('No sid provided');
    const whereQuery = Object.keys(data).map(key => where(key, '==', data[key]));
    const res = await getDocs(query(collection(tapDb, ANNOTATIONS), ...whereQuery));
    return res.empty ? false : res.docs[0];
}

/**
 * Return the Firestore query snapshot of annotations for
 * (intendedViewerID, resourceID, page). Shape matches Education's API.
 */
export async function getAnnotations(data) {
    return getDocs(query(
        collection(tapDb, ANNOTATIONS),
        where('intendedViewerID', '==', data.intendedViewerID),
        where('resourceID', '==', data.resourceID),
        where('page', '==', data.page),
    ));
}

/**
 * Create or update an annotation. Upsert keyed on `sid`. Mirrors Education's
 * soft-delete handling (entries whose `createdBy` ends with `_DELETE` are
 * treated as absent).
 */
export async function upsertAnnotation(data) {
    if (data?.NoDatabaseCopy) return true;

    const payload = { ...data, tenantID };
    if (payload.value && !payload.value_updatedAt) payload.value_updatedAt = Date.now();
    if (payload.xfdf && !payload.xfdf_updatedAt) payload.xfdf_updatedAt = Date.now();

    const collRef = collection(tapDb, ANNOTATIONS);
    const qSnap = await getDocs(query(collRef, where('sid', '==', payload.sid)));

    if (qSnap.empty) {
        const r = await addDoc(collRef, payload);
        return `Added annotation ${r.id}`;
    }

    let docId = qSnap.docs[0].id;

    if (qSnap.docs.length >= 1) {
        const entries = qSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        const deleted = entries.filter(e => e.createdBy?.endsWith('_DELETE'));
        if (deleted.length === entries.length) {
            const r = await addDoc(collRef, payload);
            return `Added annotation ${r.id}`;
        }
        if (deleted.length > 0) {
            const alive = entries.find(e => !e.createdBy?.endsWith('_DELETE'));
            if (alive) docId = alive.id;
        }
    }

    await setDoc(doc(tapDb, `${ANNOTATIONS}/${docId}`), payload, { merge: true });
    return `Updated annotation ${docId}`;
}

/**
 * Delete an annotation owned by the current user. If `archiveMode`, copies
 * the annotation into tap-db/archived-annotations first.
 */
export async function deleteAnnotation(data, archiveMode = false) {
    const ref = doc(tapDb, `${ANNOTATIONS}/${data.id}`);
    const snap = await getDoc(ref);
    if (!snap.exists()) throw new Error('No annotation');
    if (snap.data().createdBy !== auth.currentUser?.uid) {
        throw new Error('Permission denied');
    }
    if (archiveMode) {
        await addDoc(collection(tapDb, ARCHIVED_ANNOTATIONS), {
            ...snap.data(),
            archivedAt: Date.now(),
        });
    }
    await deleteDoc(ref);
    return `Deleted annotation ${data.id}`;
}

/**
 * Returns the TAP user doc from `tap-db/users/{uid}`. This doc is where
 * `redeemedCodes[]` live — the pointers to tap-db subscriptions.
 */
export async function getTapUser(uid) {
    if (!uid) return null;
    const snap = await getDoc(doc(tapDb, 'users', uid));
    return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

/**
 * Same shape as Education's `getUserSubscriptions` — bucketed by role
 * ({administrator, seatholder, teacher, student}) — backed by array-contains
 * queries against the tap-db `subscriptions` collection. TAP users land in
 * `teacher` because TAP subs have `intendedEndUserRole: [TEACHER]` and the
 * redeem flow appends the user to the `teachers` array.
 *
 * Subscriptions are per-instance (not in content-db) so we use the tap-db
 * reader here.
 */
export async function getUserSubscriptions({ uid, noCache = false } = {}) {
    return fetchUserSubscriptions({
        getEntitiesWhere: tapReader.getEntitiesWhere,
        uid,
        noCache,
    });
}

// Keep the notifications import stub so shared @repo/ui components that
// expect these names keep resolving.
export const channels = {};
export function subscribeToChannel() {
    return () => {};
}

// File-cache-marker stubs: @repo/config/Storage imports these to bust URL
// caches when teachers replace a file in Education. TAP does not author
// content, so we no-op.
export function addFileCacheMarker() {
    return Promise.resolve(Date.now());
}
export function getFileCacheMarker() {
    return Promise.resolve({ exists: () => false, data: () => null });
}

// Expose the tap-db reader in case a future feature needs it.
export const tap = {
    getEntity: tapReader.getEntity,
    getEntities: tapReader.getEntities,
    getEntitiesWhere: tapReader.getEntitiesWhere,
};
